import DCDialLogo from "icons/DCDialLogo";
import { CloseIcon } from "icons/close/CloseIcon";
import React, { useEffect, useRef, useState } from "react";
import { useUserData } from "store/slices/authSlice";
import { menuItems } from "../sidebar/menuItems";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useIsLogin } from "store/slices/commonSlice";
import { Menu } from "@headlessui/react";
import { ArrorwBottom } from "icons/sidebar/ArrorwBottom";
import UserImg from "icons/auth/user.svg";

import {
  adjustColorForHover,
  clearSlice,
  encode,
  onGetPortalSettings,
  useOutsideClick,
} from "utils/HelperFunctions";
import { UserIcon } from "icons/sidebar/UserIcon";
import { LogOutIcon } from "icons/sidebar/LogOutIcon";
import logo from "icons/smallLogo.svg";
import { TOKEN_STORAGE_KEY } from "store/api/axiosApi";
import { decode } from "utils/HelperFunctions";
import { PROFILE_URL } from "store/api/constApi";
import { RootState } from "store/store";
import { guardedNavigateGlobal } from "hooks/useNavigationGuard";
import { useTid } from "utils/testid";
import { getVisibleCustomerPortalMenuItems } from "utils/customerPortalPermissions";

const NavMenu = ({ setOpenMenu }: any) => {
  const tid = useTid();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { accentColor } = useAppSelector((state: RootState) => state.dashboard);
  const [backgroundColor, setBackgroundColor] = useState(accentColor);
  const { clusterId: cluster_id, id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  const { paymentLogo: logo1 } = useAppSelector(
    (state: RootState) => state.payment
  );
  const [rotate, setRotate] = useState<boolean>(false);
  const [isImgErr, setIsImgErr] = useState<boolean>(false);

  const isLogin = useIsLogin();
  const userData = useUserData();

  const onGetSettings = async () => {
    try {
      let payload = {
        cluster_id:
          clusterId && clusterId?.length > 0
            ? clusterId
            : decode(id || "")?.split("_")[1],
      };
      await onGetPortalSettings(payload, dispatch);
    } catch (error) {
      console.log("error =====", error);
    }
  };

  useEffect(() => {
    onGetSettings();
  }, [isLogin]);

  const handleLogOut = () => {
    clearSlice(dispatch);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.clear();
    navigate(`/${cluster_id}/login`, { replace: true });
  };

  let clusterid = cluster_id
    ? cluster_id
    : userData?.data?.accountData?.clusterid
      ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
      : id
        ? encode(decode(id || "")?.split("_")[1], "ci_")
        : "";

  const {
    accountPermission,
    accountPermissionStatus,
    accountPermissionClusterId,
  } = useAppSelector((state: RootState) => state.auth);
  const permissionClusterId =
    clusterId ||
    `${userData?.data?.accountData?.clusterid || ""}` ||
    decode(id || "")?.split("_")[1];

  const visibleMenuItems = getVisibleCustomerPortalMenuItems(menuItems, {
    accountPermission,
    accountPermissionStatus,
    accountPermissionClusterId,
    currentClusterId: permissionClusterId,
  });

  return (
    <div
      className="w-full h-[var(--shell-vh)] flex flex-col items justify-between items-start overflow-y-scroll classScroll"
      data-testid={tid("mobile-nav-menu")}
      aria-label="Navigation menu"
    >
      <div className="w-full">
        <div className="py-3 px-5 flex gap-3 justify-start items-center">
          <div
            className="cursor-pointer w-[46px] h-[46px] flex items-center justify-start rounded-full hover:!bg-bgSecondary"
            role="button"
            tabIndex={0}
            data-testid={tid("mobile-menu-close-button")}
            aria-label="Close navigation menu"
            onClick={() => setOpenMenu(false)}
          >
            <CloseIcon aria-hidden="true" />
          </div>
          <span
            role="button"
            tabIndex={0}
            data-testid={tid("mobile-menu-brand-link")}
            aria-label="Go to dashboard"
            onClick={() => {
              setOpenMenu(false);
              guardedNavigateGlobal(navigate, `/${clusterid}/dashboard`);
            }}
          >
            {location?.pathname?.split("/")[1] === "subscribe" ? (
              <DCDialLogo aria-hidden="true" />
            ) : logo1 ? (
              <span className="h-10 flex">
                <img
                  src={logo1}
                  alt="Company logo"
                  className="object-contain"
                />
              </span>
            ) : (
              <span className="h-10 flex items-center justify-center gap-2">
                <img src={logo} alt="DCDial logo" className="object-contain" />
                <div className="text-purple font-medium text-xl leading-[30px]">
                  CompanyName
                </div>
              </span>
            )}
          </span>
        </div>
        <div className="w-full h-[1px] bg-strokePrimary"></div>
        <div className="w-full pt-6 flex flex-col gap-6">
          {visibleMenuItems.map((val, i: number) => {
            let pathnames = location?.pathname?.split("/");
            let isSelected =
              `/${pathnames[2]}` === val?.url ||
              (`/${pathnames[0]}` === location?.pathname &&
                val?.id === "dashboard");
            const Icon = val?.icon;

            let clusterId = cluster_id
              ? cluster_id
              : userData?.data?.accountData?.clusterid
                ? encode(`${userData?.data?.accountData?.clusterid}`, "ci_")
                : id
                  ? encode(decode(id || "")?.split("_")[1], "ci_")
                  : "";

            return (
              <div
                role="link"
                tabIndex={0}
                aria-current={isSelected ? "page" : undefined}
                aria-label={val.title}
                data-testid={tid(`mobile-menu-${val.id}-link`)}
                onClick={() => {
                  if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                    guardedNavigateGlobal(navigate, `/${clusterId}${val.url}`);
                  } else {
                    guardedNavigateGlobal(navigate, `/${clusterId}/login`);
                  }
                  setOpenMenu(false);
                }}
                key={val.id}
                className="pr-[14px] pl-[17px] w-full cursor-pointer relative"
              >
                {isSelected ? (
                  <div className="h-10 w-[3px] bg-blue absolute left-0 rounded-tr-[6px] rounded-br-[6px] animate__animated animate__fadeIn"></div>
                ) : null}
                <div
                  className={`flex items-center gap-4 text-base font-medium leading-6 p-[7px] rounded-[5px] hover:!bg-bgSecondary ${
                    isSelected
                      ? "rounded-[5px] bg-bgSecondary text-blue animate__animated animate__fadeIn"
                      : "text-txtSecondary"
                  }`}
                >
                  <Icon
                    aria-hidden="true"
                    strokeColor={isSelected ? "var(--color-blue)" : undefined}
                  />
                  {val.title}
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-6">
          <div className="w-full h-[1px] bg-strokePrimary"></div>
        </div>
        <div className="px-6 pb-3" onClick={() => setOpenMenu(false)}>
          <div
            role="link"
            tabIndex={0}
            data-testid={tid("mobile-menu-make-payment-link")}
            aria-label="Make payment"
            style={{ backgroundColor: backgroundColor }}
            className={`cursor-pointer w-full px-4 py-[10px] rounded-md text-base text-white leading-6 font-medium focus:outline-none flex items-center gap-2 justify-center bg-btnPrimaryActive hover:bg-btnPrimaryHover`}
            onMouseEnter={(e: any) => {
              setBackgroundColor(adjustColorForHover(accentColor));
            }}
            onMouseLeave={(e: any) => {
              setBackgroundColor(accentColor);
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              if (localStorage?.getItem(TOKEN_STORAGE_KEY)) {
                guardedNavigateGlobal(navigate, `/${clusterid}/make-payment`);
              } else {
                guardedNavigateGlobal(navigate, `/${clusterid}/login`);
              }
              setOpenMenu(false);
            }}
          >
            Make Payment
          </div>
        </div>
      </div>
      <div className="w-full border-t border-strokePrimary">
        <Menu as="div" className="relative">
          <Menu.Button
            onClick={() => setRotate((state: boolean) => !state)}
            className="w-full"
            data-testid={tid("mobile-menu-profile-button")}
            aria-label="Open profile menu"
          >
            <div className="py-6 px-3 w-full flex items-center justify-between gap-3 relative">
              <div className="flex min-w-0 items-center gap-3">
                {/* <img src={avatar} alt="" /> */}
                {!isImgErr ? (
                  <div className="transition-all ease-in-out rounded-full w-[40px] h-[40px] shrink-0 text-xs cursor-pointer flex justify-center items-center overflow-hidden">
                    <img
                      id="navmenu-profile-image-1"
                      className="block rounded-full w-full h-full object-cover"
                      src={`${PROFILE_URL}/contact-assets/${
                        userData?.data?.accountData?.id
                      }?t=${Date.now()}`}
                      onError={(e) => {
                        setIsImgErr(true);
                      }}
                      alt="Profile"
                    />{" "}
                  </div>
                ) : (
                  <div className="transition-all ease-in-out rounded-full w-[40px] h-[40px] shrink-0 text-sm cursor-pointer flex justify-center items-center bg-txtParagraph text-bgPrimary">
                    <img src={UserImg} alt="" aria-hidden="true" />
                  </div>
                )}
                <div className="flex min-w-0 flex-col items-start">
                  <label className="max-w-full truncate text-txtPrimary font-bold leading-5 text-sm">
                    {(userData?.data?.accountData?.first_name || "") +
                      " " +
                      (userData?.data?.accountData?.last_name || "")}
                  </label>
                  <div className="flex min-w-0 max-w-full items-center gap-1 text-sm leading-5 font-normal text-txtSecondary">
                    <span className="shrink-0">ID</span>
                    <span className="truncate">
                      {userData?.data?.accountData?.accountid}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`shrink-0 ${rotate ? "rotate-180" : "rotate-0"}`}>
                <ArrorwBottom aria-hidden="true" />
              </div>
            </div>
          </Menu.Button>

          <Menu.Items
            className="absolute rounded-[12px] pt-[8px] z-10 bottom-[80px] left-[14px]  mt-2 w-[90%] origin-top-right divide-y divide-gray-100 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            data-testid={tid("mobile-menu-profile-menu")}
          >
            <div className="py-1 w-full">
              <Menu.Item
                as="div"
                className="px-4 py-3 border-b border-strokePrimary-200"
              >
                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* <img src={avatar} alt="" /> */}
                  {!isImgErr ? (
                    <div className="transition-all ease-in-out rounded-full w-[40px] h-[40px] shrink-0 text-xs cursor-pointer flex justify-center items-center overflow-hidden">
                      <img
                        id="navmenu-profile-image-2"
                        className="block rounded-full w-full h-full object-cover"
                        src={`${PROFILE_URL}/contact-assets/${
                          userData?.data?.accountData?.id
                        }?t=${Date.now()}`}
                        onError={(e) => {
                          setIsImgErr(true);
                        }}
                        alt="Profile"
                      />{" "}
                    </div>
                  ) : (
                    <div className="transition-all ease-in-out rounded-full w-[40px] h-[40px] shrink-0 text-sm cursor-pointer flex justify-center items-center bg-txtParagraph text-bgPrimary">
                      <img src={UserImg} alt="" aria-hidden="true" />
                    </div>
                  )}
                  <div className="flex flex-col items-start">
                    <label className="text-txtPrimary font-medium leading-5 text-sm">
                      {isLogin
                        ? (userData?.data?.accountData?.first_name || "") +
                          " " +
                          (userData?.data?.accountData?.last_name || "")
                        : "--"}
                    </label>
                    <div className="flex items-center gap-1 text-sm leading-5 font-normal text-txtSecondary">
                      <span>ID</span>
                      <span>
                        {isLogin ? userData?.data?.accountData?.accountid : ""}
                      </span>
                    </div>
                  </div>
                </div>
              </Menu.Item>
              <Menu.Item
                as="div"
                className="py-1 px-[6px] cursor-pointer border-b border-strokePrimary-200"
                data-testid={tid("mobile-menu-profile-link")}
                aria-label="My profile"
                onClick={() => {
                  navigate(`/${clusterid}/my-profile`);
                  setOpenMenu(false);
                  setRotate(!rotate);
                }}
              >
                {({ active }) => (
                  <div className="py-[9px] px-[10px] flex items-center gap-2">
                    <UserIcon aria-hidden="true" />
                    <div className="text-sm leading-[20px] text-txtPrimary">
                      My profile
                    </div>
                  </div>
                )}
              </Menu.Item>
              <Menu.Item
                as="div"
                className="py-1 px-[6px] cursor-pointer"
                data-testid={tid("mobile-menu-logout-button")}
                aria-label="Log out"
                onClick={() => {
                  handleLogOut();
                  setRotate(!rotate);
                }}
              >
                {({ active }) => (
                  <div className="py-[9px] px-[10px] flex items-center gap-2">
                    <LogOutIcon aria-hidden="true" />
                    <div className="text-sm leading-[20px] text-txtPrimary">
                      Log out
                    </div>
                  </div>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Menu>
      </div>
    </div>
  );
};

export default NavMenu;
